import { useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { EMPTY, median, newestFirst, propertyById, type Evidence } from '@kilroy/shared';

import { db } from '../db/db';
import { useSession } from '../app/session';
import { useUsers } from '../hooks/useUsers';
import { getStatus, subscribe } from '../sync/engine';
import { AssignmentCard } from '../components/AssignmentCard';
import { EvidenceList } from '../components/EvidenceList';

/* Home. Two questions, in the order they get asked at 6am: what am I meant to
 * walk, and what have I got so far.
 *
 * The glance row at the top exists because the previous version had no anchor
 * — the eye landed on chrome. Three numbers about your own day is a better
 * first thing to see than a sync state you can do nothing about.
 */

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

export function FieldDay() {
  const { user, propertyId } = useSession();
  const status = useSyncExternalStore(subscribe, getStatus);
  const { userById } = useUsers();
  const navigate = useNavigate();
  const location = useLocation();

  const [toast, setToast] = useState<string | null>(
    (location.state as { toast?: string } | null)?.toast ?? null,
  );
  useEffect(() => {
    if (!toast) return;
    navigate('.', { replace: true, state: {} });
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  const today = useLiveQuery(
    async () => {
      const rows = await db.evidence.where('captured_at').aboveOrEqual(startOfToday()).toArray();
      // Newest first. Superposition and reverse-chron agree.
      return rows.sort(newestFirst);
    },
    [],
    [] as Evidence[],
  );

  const allEvidence = useLiveQuery(() => db.evidence.toArray(), [], [] as Evidence[]);

  const assignments = useLiveQuery(
    async () => {
      const rows = await db.assignments.toArray();
      return rows.filter(
        (a) =>
          (a.assigned_to === user.id || !a.assigned_to) &&
          (a.current_status === 'open' || a.current_status === 'in_progress'),
      );
    },
    [user.id],
    [],
  );

  const acc = median(today.filter((e) => e.gps_accuracy_m >= 0).map((e) => e.gps_accuracy_m));
  const queued = useLiveQuery(() => db.outbox.count(), [], 0);
  const done = assignments.filter((a) => a.current_status === 'in_progress').length;

  return (
    <div className="screen">
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}

      {/* The anchor. Your day, in three numbers.
       *
       * Accuracy carries a tone because it is the one number here that means
       * something beyond counting: at or under 10 m a record counts toward the
       * training set, and knowing that at a glance changes whether you wait
       * thirty seconds for a better fix. It is never a judgement on the
       * person — a ±40 m fix in a canyon is a good record of a hard place. */}
      <div className="mod">
        <div className="card glance">
          <div className="gl">
            <div className="gl__v">{today.length}</div>
            <div className="gl__l">Captured</div>
          </div>
          <div className="gl">
            <div className={`gl__v${acc != null && acc <= 10 ? ' gl__v--win' : ''}`}>
              {acc == null ? '—' : <>±{Math.round(acc)}<s> m</s></>}
            </div>
            <div className="gl__l">Median accuracy</div>
          </div>
          <div className="gl">
            <div className="gl__v">
              {done}
              <s>/{assignments.length}</s>
            </div>
            <div className="gl__l">Assignments</div>
          </div>
        </div>
        {queued > 0 && (
          <p className="fd__queued mono">
            {queued} waiting to send · they are safe on this phone until they do
          </p>
        )}
      </div>

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Assigned to you</h2>
          {assignments.length > 3 && (
            <button type="button" className="mod__a" onClick={() => navigate('/assigned')}>
              See all
            </button>
          )}
        </div>

        {assignments.length === 0 ? (
          <div className="card empty">
            <div className="empty__t">{EMPTY.assignments}</div>
            <div className="empty__h">Capture anything you find — it doesn’t need one.</div>
          </div>
        ) : (
          assignments
            .slice(0, 3)
            .map((a) => (
              <AssignmentCard
                key={a.id}
                assignment={a}
                evidence={allEvidence}
                author={userById(a.created_by)}
              />
            ))
        )}
      </section>

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Captured today</h2>
          <span className="mod__n">
            {today.length} record{today.length === 1 ? '' : 's'}
          </span>
        </div>

        <EvidenceList evidence={today} empty={EMPTY.today} hint={EMPTY.todayAction} />
      </section>

      {/* The narration only speaks when there is something to narrate. */}
      {status.narration && status.phase !== 'synced' && (
        <p className="hint" style={{ marginTop: 'var(--s-4)', padding: '0 2px' }}>
          {status.narration}
        </p>
      )}

      <p className="hint" style={{ marginTop: 'var(--s-5)', padding: '0 2px' }}>
        {propertyById(propertyId).name} · saved on this phone the moment you tap Save
      </p>
    </div>
  );
}
